import React from "react";
import { useParams } from "react-router";

import { ROUTES } from "@config/app.routing";

import { Params, Variant } from "@typings/contractChecklist";

import { buildUrl } from "@services/BuildUrl";

type BuildPath = (route?: ROUTES) => string;

interface ChecklistRouteContext {
  params: Params;
  buildPath: BuildPath;
}

const ChecklistRouteContext = React.createContext<ChecklistRouteContext | null>(
  null,
);

const ChecklistRouteProvider = (props: {
  children: React.ReactNode;
}): React.ReactElement => {
  const params = useParams<Params>();

  const buildPath = React.useCallback<BuildPath>(
    route => {
      return buildUrl(
        `${ROUTES.CLIENT_DASHBOARD}${ROUTES.CLIENT_CHECKLIST}${route ?? ""}`,
        {
          keyType: "colon",
        },
      )
        .setPath("contractUuid", params.contractUuid ?? "")
        .setPath<Variant>("variant", params.variant ?? "")
        .done();
    },
    [params],
  );

  return (
    <ChecklistRouteContext.Provider value={{ params, buildPath }}>
      {props.children}
    </ChecklistRouteContext.Provider>
  );
};

const useChecklistRoute = (): ChecklistRouteContext => {
  const context = React.useContext(ChecklistRouteContext);

  if (!context) {
    throw new Error(
      "useChecklistRoute must be used within a ChecklistRouteProvider",
    );
  }

  return context;
};

export { ChecklistRouteProvider, useChecklistRoute };
